import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from '../utils/api';
import {
  UserGroupIcon,
  FolderIcon,
  ArrowLeftOnRectangleIcon,
  BellIcon,
  BugAntIcon,
  InboxIcon,
  HomeIcon,
  ViewColumnsIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';

const ManagerNavbar = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const notificationRef = useRef(null);
  const bugCodeCache = useRef(new Map());
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  useEffect(() => {
    if (user) {
      fetchNotifications();
      const interval = setInterval(fetchNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  useEffect(() => {
    const onResize = () => {
      setSidebarCollapsed(window.innerWidth < 1024);
    };
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Handle click outside to close notifications
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };

    if (showNotifications) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showNotifications]);

  const fetchNotifications = async () => {
    try {
      const { data } = await axios.get('/api/notifications');
      const list = Array.isArray(data.data) ? data.data : [];
      const seen = new Map();
      const deduped = [];
      for (const n of list) {
        const actorId = n.actorUserId?._id || n.actorUserId || n.actor?._id || n.actor?.id || '';
        const bugRaw = n.bugId ?? n.bug;
        const bugId = typeof bugRaw === 'string' ? bugRaw : (bugRaw?._id || bugRaw?.bugId || '');
        const type = n.actionType || n.type || '';
        const bucket = Math.floor(new Date(n.createdAt).getTime() / (15 * 60 * 1000));
        const key = `${actorId}|${bugId}|${type}|${bucket}`;
        if (!seen.has(key)) {
          seen.set(key, true);
          deduped.push(n);
        }
      }
      // Enrich bug codes for display
      const enriched = await Promise.all(
        deduped.map(async (n) => {
          const bugRaw = n.bugId ?? n.bug;
          let displayBugCode =
            (typeof bugRaw === 'string'
              ? (/^BUG-/i.test(bugRaw) ? bugRaw : '')
              : (bugRaw?.bugId || '')) ||
            (n.bugCode || n.code || '');
          if (!displayBugCode && typeof bugRaw === 'string') {
            if (bugCodeCache.current.has(bugRaw)) {
              displayBugCode = bugCodeCache.current.get(bugRaw);
            } else {
              try {
                const res = await axios.get(`/api/bugs/${bugRaw}`);
                const code = res.data?.data?.bugId || '';
                if (code) {
                  bugCodeCache.current.set(bugRaw, code);
                  displayBugCode = code;
                }
              } catch (_) { }
            }
          }
          return { ...n, displayBugCode };
        })
      );
      setNotifications(enriched);
      const count = enriched.filter(n => !n.read).length;
      setUnreadCount(Math.min(99, count));
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };

  const markAsRead = async (id) => {
    try {
      await axios.put(`/api/notifications/${id}/read`);
      fetchNotifications();
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };
  const formatCompact = (n) => {
    const bugRaw = n.bugId ?? n.bug ?? n.bugCode ?? n.code;
    const fallbackCode = typeof bugRaw === 'string' ? bugRaw : (bugRaw?.bugId || bugRaw?._id || '');
    const bugCode = n.displayBugCode || fallbackCode;
    const actor = n.actorUserId?.name || n.actor?.name || n.user?.name || n.changedBy?.name || (typeof n.actorName === 'string' ? n.actorName : '') || 'Someone';
    const type = n.actionType || n.type || '';
    const status = n.status || n.newStatus || '';
    if (type === 'bug_fixed' || status?.toLowerCase() === 'fixed') {
      return `${actor} fixed ${bugCode}`;
    }
    if (type === 'status_changed' || type === 'bug_status_change' || status) {
      return `${actor} changed status of ${bugCode} to ${status}`;
    }
    if (type === 'bug_reported') {
      return `${actor} reported ${bugCode}`;
    }
    if (type === 'bug_assigned') {
      return `${actor} assigned ${bugCode}`;
    }
    if (bugCode) {
      return `${actor} updated ${bugCode}`;
    }
    return n.title || n.message || 'Notification';
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path) => location.pathname === path;

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Left Sidebar Navigation */}
      <aside className={`${sidebarCollapsed ? 'w-[72px]' : 'w-[220px]'} bg-[#1E293B] text-gray-200 flex flex-col sticky top-0 h-screen border-r border-white/5 transition-[width] duration-200`}>
        <div className={`${sidebarCollapsed ? 'px-3' : 'px-5'} h-16 flex items-center justify-between border-b border-white/5`}>
          <Link to="/manager/dashboard" className="flex items-center gap-2 min-w-0">
            <img src="/assets/logo-final.jpg" alt="Bug Tracker" className="h-8 w-8 rounded-full" />
            {!sidebarCollapsed && (
              <div className="flex flex-col min-w-0">
                <span className="text-[15px] font-semibold text-[#E2E8F0] truncate">Bug Tracker</span>
              </div>
            )}
          </Link>
          <button
            onClick={() => setSidebarCollapsed(v => !v)}
            className="inline-flex h-8 w-8 items-center justify-center rounded hover:bg-[#334155] transition-colors"
            title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            type="button"
          >
            <ChevronDownIcon className={`h-4 w-4 text-[#CBD5E1] transition-transform ${sidebarCollapsed ? '-rotate-90' : 'rotate-90'}`} />
          </button>
        </div>

        {/* Navigation Links */}
        <nav className="py-3 flex flex-col h-full">
          <div className="flex-1 overflow-y-auto">
            {/* Overview Section */}
            {!sidebarCollapsed && (
              <div className="px-5 pt-2 pb-2">
                <h3 className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8]">Overview</h3>
              </div>
            )}
            <Link to="/manager/dashboard" className="block">
              <div className={`mx-2 my-1 flex items-center gap-3 rounded-md px-3 py-2 text-[14px] font-medium transition-colors ${isActive('/manager/dashboard') ? 'bg-[#4F46E5] text-[#E2E8F0]' : 'text-[#E2E8F0] hover:bg-[#334155]'} ${sidebarCollapsed ? 'justify-center' : ''}`}>
                <HomeIcon className={`h-5 w-5 flex-shrink-0 ${isActive('/manager/dashboard') ? 'text-[#E2E8F0]' : 'text-[#CBD5E1]'}`} />
                {!sidebarCollapsed && <span className={`${isActive('/manager/dashboard') ? 'font-semibold' : 'font-medium'}`}>Dashboard</span>}
              </div>
            </Link>

            {/* Work Items Section */}
            {!sidebarCollapsed && (
              <div className="px-5 pt-4 pb-2">
                <h3 className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8]">Work Items</h3>
              </div>
            )}
            <Link to="/manager/bugs" className="block">
              <div className={`mx-2 my-1 flex items-center gap-3 rounded-md px-3 py-2 text-[14px] font-medium transition-colors ${isActive('/manager/bugs') ? 'bg-[#4F46E5] text-[#E2E8F0]' : 'text-[#E2E8F0] hover:bg-[#334155]'} ${sidebarCollapsed ? 'justify-center' : ''}`}>
                <BugAntIcon className={`h-5 w-5 flex-shrink-0 ${isActive('/manager/bugs') ? 'text-[#E2E8F0]' : 'text-[#CBD5E1]'}`} />
                {!sidebarCollapsed && <span className={`${isActive('/manager/bugs') ? 'font-semibold' : 'font-medium'}`}>Bugs</span>}
              </div>
            </Link>

            <Link to="/manager/kanban" className="block">
              <div className={`mx-2 my-1 flex items-center gap-3 rounded-md px-3 py-2 text-[14px] font-medium transition-colors ${isActive('/manager/kanban') ? 'bg-[#4F46E5] text-[#E2E8F0]' : 'text-[#E2E8F0] hover:bg-[#334155]'} ${sidebarCollapsed ? 'justify-center' : ''}`}>
                <ViewColumnsIcon className={`h-5 w-5 flex-shrink-0 ${isActive('/manager/kanban') ? 'text-[#E2E8F0]' : 'text-[#CBD5E1]'}`} />
                {!sidebarCollapsed && <span className={`${isActive('/manager/kanban') ? 'font-semibold' : 'font-medium'}`}>Kanban View</span>}
              </div>
            </Link>

            {/* Organization Section */}
            {!sidebarCollapsed && (
              <div className="px-5 pt-4 pb-2">
                <h3 className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8]">Organization</h3>
              </div>
            )}
            <Link to="/manager/projects" className="block">
              <div className={`mx-2 my-1 flex items-center gap-3 rounded-md px-3 py-2 text-[14px] font-medium transition-colors ${isActive('/manager/projects') ? 'bg-[#4F46E5] text-[#E2E8F0]' : 'text-[#E2E8F0] hover:bg-[#334155]'} ${sidebarCollapsed ? 'justify-center' : ''}`}>
                <FolderIcon className={`h-5 w-5 flex-shrink-0 ${isActive('/manager/projects') ? 'text-[#E2E8F0]' : 'text-[#CBD5E1]'}`} />
                {!sidebarCollapsed && <span className={`${isActive('/manager/projects') ? 'font-semibold' : 'font-medium'}`}>Projects</span>}
              </div>
            </Link>

            <Link to="/manager/employees" className="block">
              <div className={`mx-2 my-1 flex items-center gap-3 rounded-md px-3 py-2 text-[14px] font-medium transition-colors ${isActive('/manager/employees') ? 'bg-[#4F46E5] text-[#E2E8F0]' : 'text-[#E2E8F0] hover:bg-[#334155]'} ${sidebarCollapsed ? 'justify-center' : ''}`}>
                <UserGroupIcon className={`h-5 w-5 flex-shrink-0 ${isActive('/manager/employees') ? 'text-[#E2E8F0]' : 'text-[#CBD5E1]'}`} />
                {!sidebarCollapsed && <span className={`${isActive('/manager/employees') ? 'font-semibold' : 'font-medium'}`}>Employees</span>}
              </div>
            </Link>

            {/* Communication Section */}
            {!sidebarCollapsed && (
              <div className="px-5 pt-4 pb-2">
                <h3 className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8]">Communication</h3>
              </div>
            )}
            <Link to="/manager/inbox" className="block">
              <div className={`mx-2 my-1 flex items-center gap-3 rounded-md px-3 py-2 text-[14px] font-medium transition-colors ${isActive('/manager/inbox') ? 'bg-[#4F46E5] text-[#E2E8F0]' : 'text-[#E2E8F0] hover:bg-[#334155]'} ${sidebarCollapsed ? 'justify-center' : ''}`}>
                <InboxIcon className={`h-5 w-5 flex-shrink-0 ${isActive('/manager/inbox') ? 'text-[#E2E8F0]' : 'text-[#CBD5E1]'}`} />
                {!sidebarCollapsed && <span className={`${isActive('/manager/inbox') ? 'font-semibold' : 'font-medium'}`}>Discussion & Notifications</span>}
              </div>
            </Link>
          </div>
        </nav>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Top Header */}
        <div className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
          <div className="px-6 py-4 flex items-center justify-between gap-4">
            <div className="flex items-center gap-4 flex-1">
              <h2 className="text-lg font-semibold text-gray-900 whitespace-nowrap">
                {location.pathname === '/manager/dashboard' && 'Dashboard'}
                {location.pathname === '/manager/employees' && 'Employees'}
                {location.pathname === '/manager/projects' && 'Projects'}
                {location.pathname === '/manager/bugs' && 'Bugs'}
                {location.pathname === '/manager/kanban' && 'Kanban View'}
              </h2>
            </div>

            {/* Right side: Notification Icon and User Profile */}
            <div className="flex items-center space-x-4">
              {/* Notification Icon */}
              <div className="relative" ref={notificationRef}>
                <button
                  onClick={() => {
                    const next = !showNotifications;
                    setShowNotifications(next);
                    if (next) {
                      fetchNotifications();
                    }
                  }}
                  className="relative p-2 text-gray-600 hover:text-gray-900 transition-colors"
                >
                  <BellIcon className="h-6 w-6" />
                  {unreadCount > 0 && (
                    <span className="absolute top-1 right-1 inline-flex items-center justify-center h-4 w-4 text-[10px] font-bold leading-none text-white transform translate-x-1 -translate-y-1 bg-red-600 rounded-full ring-2 ring-white">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </button>

                {showNotifications && (
                  <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-xl z-50 max-h-96 overflow-y-auto border border-gray-100">
                    <div className="p-4 border-b">
                      <h3 className="text-lg font-semibold">Notifications</h3>
                    </div>
                    {notifications.length === 0 ? (
                      <div className="p-4 text-center text-gray-500">No notifications</div>
                    ) : (
                      <div className="divide-y">
                        {notifications.slice(0, 10).map((notification) => (
                          <div
                            key={notification._id}
                            className={`p-3 hover:bg-gray-50 cursor-pointer ${!notification.read ? 'bg-indigo-50/30' : ''
                              }`}
                            onClick={() => {
                              markAsRead(notification._id);
                              // Navigate based on notification content if needed
                              if (notification.bugId || notification.bug) {
                                const bugId = notification.bugId?._id || notification.bugId || notification.bug?._id;
                                if (bugId) navigate(`/manager/bugs`); // Managers go to manager bug list
                              }
                              setShowNotifications(false);
                            }}
                          >
                            <div className="flex items-center gap-2">
                              {!notification.read && <span className="h-1.5 w-1.5 bg-indigo-600 rounded-full flex-shrink-0"></span>}
                              <p className={`text-xs ${!notification.read ? 'font-bold text-gray-900' : 'font-medium text-gray-700'} truncate whitespace-nowrap max-w-[200px]`}>
                                {formatCompact(notification)}
                              </p>
                              <span className="ml-auto text-[10px] text-gray-500 whitespace-nowrap">
                                {(() => {
                                  const d = new Date(notification.createdAt);
                                  if (isNaN(d.getTime())) return '';
                                  return `${d.getDate()} ${d.toLocaleString('en-US', { month: 'short' })} ${d.getFullYear()}, ${d.toLocaleString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}`;
                                })()}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* User Profile */}
              <div className="flex items-center space-x-3 pl-4 border-l border-gray-200">
                <div className="text-right">
                  <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                  <p className="text-xs text-gray-500 capitalize">{user?.role}</p>
                </div>
                <button
                  onClick={handleLogout}
                  className="p-2 text-gray-600 hover:text-red-600 transition-colors"
                  title="Logout"
                >
                  <ArrowLeftOnRectangleIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Page Content */}
        <div className="flex-1 overflow-y-auto bg-gray-50">
          {children}
        </div>
      </div>
    </div>
  );
};

export default ManagerNavbar;
